export default {
  computed: {
    maxDesembolsos() {
      if (!this.resultados || this.resultados.length === 0) return 0;
      const max = Math.max(
        ...this.resultados.map((item) =>
          item.desembolsos ? item.desembolsos.length : 0
        )
      );
      // Limitar a máximo 5 desembolsos
      return Math.min(max, 5);
    },
    totalMonto() {
      return this.resultados.reduce((total, item) => {
        if (item._esSubtotal) return total; // Ignorar subtotales para evitar doble conteo

        // Solo validar estado para Financiamiento normal (no ANR, no reportes especiales)
        if (this.esOperacion === 'Financiamiento' && !this.esEstadoValidoParaMonto(item)) {
          return total;
        }

        const monto = this.getTipoMonto(
          item.parametros,
          this.esOperacion === 'ANR' ? 'monto_total_solicitado' : 'monto'
        );
        return total + parseFloat(monto || 0);
      }, 0);
    },
    totalMontoAprobado() {
      return this.resultados.reduce((total, item) => {
        if (item._esSubtotal) return total; // Ignorar subtotales para evitar doble conteo

        // Intentar obtener monto aprobado de parametros primero, luego de item.monto_aprobado
        let monto = 0;
        if (item.parametros) {
          monto = parseFloat(this.getTipoMonto(item.parametros, 'monto_aprobado')) || 0;
        }
        if (!monto && item.monto_aprobado) {
          monto = parseFloat(item.monto_aprobado) || 0;
        }

        return total + monto;
      }, 0);
    },
    totalMontoPendiente() {
      return this.resultados.reduce((total, item) => {
        if (item._esSubtotal) return total; // Ignorar subtotales para evitar doble conteo
        const montoPendiente = this.getPendienteDesembolso(item);
        return total + parseFloat(montoPendiente || 0);
      }, 0);
    },
    totalGastoAdministrativo() {
      return this.resultados.reduce((total, item) => {
        if (item._esSubtotal === true) return total; // Ignorar subtotales para evitar doble conteo
        // Solo usar honorarios_mf (no sumar administrativo porque son el mismo valor)
        const honorariosMf = item.gastos && item.gastos.honorarios_mf ? parseFloat(item.gastos.honorarios_mf) : 0;
        return total + honorariosMf;
      }, 0);
    },
    totalGastoIibb() {
      return this.resultados.reduce((total, item) => {
        if (item._esSubtotal) return total; // Ignorar subtotales para evitar doble conteo
        // Solo usar otros_gastos (no sumar iibb porque son el mismo valor)
        const otrosGastos = item.gastos && item.gastos.otros_gastos ? parseFloat(item.gastos.otros_gastos) : 0;
        return total + otrosGastos;
      }, 0);
    },
    totalGastoOtorgamiento() {
      return this.resultados.reduce((total, item) => {
        if (item._esSubtotal) return total; // Ignorar subtotales para evitar doble conteo
        // Solo usar gastos_escribano (no sumar otorgamiento porque son el mismo valor)
        const gastosEscribano = item.gastos && item.gastos.gastos_escribano ? parseFloat(item.gastos.gastos_escribano) : 0;
        return total + gastosEscribano;
      }, 0);
    },
    totalMontoLiquidado() {
      return this.resultados.reduce((total, item) => {
        if (item._esSubtotal) return total; // Ignorar subtotales para evitar doble conteo

        // Solo validar estado para Financiamiento normal (no ANR, no reportes especiales)
        if (this.esOperacion === 'Financiamiento' && !this.esEstadoValidoParaMonto(item)) {
          return total;
        }

        // Si hay desembolsos, calcular como suma de (monto + gastos) de cada desembolso
        if (item.desembolsos && Array.isArray(item.desembolsos) && item.desembolsos.length > 0) {
          const montoLiquidado = item.desembolsos.reduce((subtotal, desembolso) => {
            const monto = parseFloat(desembolso.monto) || 0;
            const gastos = parseFloat(desembolso.gastos) || 0;
            return subtotal + monto + gastos;
          }, 0);
          return total + montoLiquidado;
        }

        // Si no hay desembolsos, usar el monto del parametro menos los gastos totales
        let monto = 0;
        if (item.parametros && item.parametros.monto && item.parametros.monto[0]) {
          monto = parseFloat(item.parametros.monto[0]) || 0;
        }

        let totalGastos = 0;
        if (item.gastos && item.gastos !== null && typeof item.gastos === 'object') {
          if (item.gastos.total) {
            totalGastos = parseFloat(item.gastos.total) || 0;
          }
        }

        const montoLiquidado = monto - totalGastos;
        return total + montoLiquidado;
      }, 0);
    },
    incluyeGastoAdministrativo() {
      return this.headers.some(header => header.value === 'gasto_administrativo');
    },
    incluyeGastoIibb() {
      return this.headers.some(header => header.value === 'gasto_iibb');
    },
    incluyeGastoOtorgamiento() {
      return this.headers.some(header => header.value === 'gasto_otorgamiento');
    },
    // Totales específicos para Info Cte y No Cte
    totalMontoOtorgado() {
      return this.resultados.reduce((total, item) => {
        if (item._esSubtotal) return total; // Ignorar subtotales para evitar doble conteo
        return total + parseFloat(item.monto_otorgado || 0);
      }, 0);
    },
    totalCapitalCorriente() {
      return this.resultados.reduce((total, item) => {
        if (item._esSubtotal) return total; // Ignorar subtotales para evitar doble conteo
        return total + parseFloat(item.capital_corriente || 0);
      }, 0);
    },
    totalCapitalNoCorriente() {
      return this.resultados.reduce((total, item) => {
        if (item._esSubtotal) return total; // Ignorar subtotales para evitar doble conteo
        return total + parseFloat(item.capital_no_corriente || 0);
      }, 0);
    },
    totalSaldoCapitalTotal() {
      return this.resultados.reduce((total, item) => {
        if (item._esSubtotal) return total; // Ignorar subtotales para evitar doble conteo
        return total + parseFloat(item.saldo_capital_total || 0);
      }, 0);
    },
  },
  methods: {
    /**
     * Valida si el estado del item permite mostrar el monto otorgado.
     * Solo los estados "Otorgado" y "Cancelado" tienen monto otorgado > 0.
     * @param {Object} item - El item a validar
     * @returns {boolean} - true si el estado es válido para mostrar monto
     */
    esEstadoValidoParaMonto(item) {
      if (!item || !item.estado) return false;
      const estadosValidos = ['Otorgado', 'Cancelado'];
      return estadosValidos.includes(item.estado);
    },
    /**
     * Obtiene el monto otorgado del item solo si el estado es válido.
     * @param {Object} item - El item del que obtener el monto
     * @param {string} tipo - El tipo de monto a obtener (default: 'monto')
     * @returns {number} - El monto o 0 si el estado no es válido
     */
    getMontoOtorgadoConValidacion(item, tipo = 'monto') {
      if (!this.esEstadoValidoParaMonto(item)) {
        return 0;
      }
      return this.getTipoMonto(item.parametros, tipo);
    },
    agruparPorFideicomiso(resultados) {
      if (!resultados || resultados.length === 0) return [];

      const resultadosConFideicomisos = [];
      let fideicomisoActual = null;

      resultados.forEach((item, index) => {
        // Obtener el nombre del fideicomiso del item
        const nombreFideicomiso = item.fideicomiso || item.fideicomisoName || '';

        // Si el item es un subtotal, no crear header de fideicomiso
        // (el subtotal ya muestra la información de la operatoria)
        if (item._esSubtotal) {
          resultadosConFideicomisos.push(item);
          return;
        }

        // Si cambia el fideicomiso, agregar una fila de encabezado
        if (nombreFideicomiso && nombreFideicomiso !== fideicomisoActual) {
          fideicomisoActual = nombreFideicomiso;
          resultadosConFideicomisos.push({
            _esFideicomisoHeader: true,
            _nombreFideicomiso: nombreFideicomiso,
            fideicomiso: nombreFideicomiso,
            // Agregar un ID único para evitar problemas con v-for
            _id: `fideicomiso_${index}_${nombreFideicomiso}`
          });
        }

        // Mapear campos adicionales para que coincidan con los headers
        const mappedItem = {
          ...item,
          // Mapear campo departamento para compatibilidad con headers
          departamento: this.getDepartamentoNombreFromItem(item),
          // Mapear otros campos comunes si no existen
          legajo: item.legajo || item.id,
          operatoria_linea: item.operatoria_linea || item.operatoria,
        };

        // Agregar el item mapeado
        resultadosConFideicomisos.push(mappedItem);
      });

      return resultadosConFideicomisos;
    },
    // Helper para obtener departamento desde el item
    getDepartamentoNombreFromItem(item) {
      // Intentar obtener departamento desde diferentes estructuras
      // ✅ PRIMERO: Si el item ya tiene el campo departamento del backend, usarlo
      if (item.departamento && item.departamento !== '') {
        return item.departamento;
      }

      if (item.localidad && item.localidad.departamento && item.localidad.departamento.nombre) {
        return item.localidad.departamento.nombre;
      }
      if (item.cliente && item.cliente.localidad && item.cliente.localidad.departamento && item.cliente.localidad.departamento.nombre) {
        return item.cliente.localidad.departamento.nombre;
      }
      // Fallback a localidad.nombre si no hay departamento
      if (item.cliente && item.cliente.localidad && typeof item.cliente.localidad === 'object' && item.cliente.localidad.nombre) {
        return item.cliente.localidad.nombre;
      }
      if (item.cliente && item.cliente.localidad && typeof item.cliente.localidad === 'string') {
        return item.cliente.localidad;
      }
      if (item.localidad && typeof item.localidad === 'string') {
        return item.localidad;
      }
      if (item.localidad && item.localidad.nombre) {
        return item.localidad.nombre;
      }
      return '-';
    },
    // El método getDepartamentoNombre se mantiene en BaseReportTable para uso en templates
    agruparPorFideicomiso_OLD(resultados) {
    },
    formatoNumero(valor) {
      const numero = Number(valor);
      if (isNaN(numero)) return '-';
      return numero.toLocaleString('es-AR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatDates(utc) {
      if (!utc) return '';
      // Import the utility function
      const { formatDateUnix } = require('../../common/util');
      return formatDateUnix(utc);
    },
    getTipoMonto(parametros, tipo = 'monto_total_solicitado') {
      if (!parametros) {
        return 0;
      }

      // Verificar si el tipo de parámetro existe en los parámetros
      if (!parametros[tipo]) {
        return 0;
      }

      // Los parámetros vienen como arrays, tomar el primer elemento
      const monto = parametros[tipo][0];
      return monto || 0;
    },
    getPendienteDesembolso(item) {
      if (!item) {
        return 0;
      }

      // Obtener monto aprobado con fallback a item.monto_aprobado
      let montoAprobado = 0;
      if (item.parametros) {
        montoAprobado = parseFloat(this.getTipoMonto(item.parametros, 'monto_aprobado')) || 0;
      }
      if (!montoAprobado && item.monto_aprobado) {
        montoAprobado = parseFloat(item.monto_aprobado) || 0;
      }

      // Obtener monto otorgado
      let montoOtorgado = 0;

      // Solo validar estado para Financiamiento normal (no ANR, no reportes especiales)
      const debeValidarEstado = this.esOperacion === 'Financiamiento';

      if (!debeValidarEstado || this.esEstadoValidoParaMonto(item)) {
        // Para ANR: el monto otorgado es la suma de todos los desembolsos
        // Para otros reportes: usar item.parametros.monto
        if (item.desembolsos && Array.isArray(item.desembolsos) && item.desembolsos.length > 0) {
          // Si tiene desembolsos, sumar todos (lógica ANR)
          montoOtorgado = item.desembolsos.reduce((total, desembolso) => {
            return total + (parseFloat(desembolso.monto) || 0);
          }, 0);
        } else if (item.parametros) {
          // Si no tiene desembolsos, usar item.parametros.monto (lógica Financiamiento)
          montoOtorgado = parseFloat(this.getTipoMonto(item.parametros, 'monto')) || 0;
        }
      }

      // Monto pendiente = Monto aprobado - Monto otorgado
      return montoAprobado - montoOtorgado;
    },
    getDesembolso(item, numeroDesembolso) {
      if (!item.desembolsos || !item.desembolsos.length) return null;
      // Usar índice del array (numeroDesembolso - 1) en lugar de buscar por propiedad
      return item.desembolsos[numeroDesembolso - 1] || null;
    },
    getTotalDesembolso(numeroDesembolso) {
      return this.resultados.reduce((total, item) => {
        if (item._esSubtotal) return total; // Ignorar subtotales para evitar doble conteo

        // Solo validar estado para Financiamiento normal (no ANR, no reportes especiales)
        if (this.esOperacion === 'Financiamiento' && !this.esEstadoValidoParaMonto(item)) {
          return total;
        }

        const desembolso = this.getDesembolso(item, numeroDesembolso);
        return total + (desembolso ? parseFloat(desembolso.monto || 0) : 0);
      }, 0);
    },
  },
};